<template>
  <section class="gallery w-full overflow-hidden bg-white md:rounded lg:grid lg:gap-4 lg:grid-cols-[1fr_92px] lg:py-4 lg:px-5">
    <client-only>
      <div :id="galleryID" class="main-image relative">
        <div class="flex justify-center items-center">
          <swiper-container
            ref="mainImageRef"
            class="max-h-full max-w-100dvw w-full !sm:max-w-600px !md:max-w-376px !lg:max-w-426px xl:!max-w-596px"
            v-bind="MAIN_IMAGE_SWIPER_OPTIONS"
            @swiperafterinit="handleMainImageSwiperInit"
            @swiperslidechange="handleMainImageSwiperSlideChange"
          >
            <swiper-slide v-for="img in props.product.images" :key="img" class="cursor-pointer">
              <nd-img :src="`fn:${img}`" :alt="props.product?.product_name" class="w-full aspect-1 object-cover bg-gray-30" />
            </swiper-slide>
          </swiper-container>
        </div>
        <div v-if="app.isMobile || app.isTablet" class="overlay absolute top-0 left-0 w-full h-full z-1 pointer-events-none">
          <favorite-button
            v-if="app.isMobile"
            :id="props.product.pid!"
            class="absolute top-2 right-2 pointer-events-auto"
            :is-favorited="props.product?.is_favorite || false"
          />
          <div class="absolute bottom-3 right-4 bg-black-30 text-white px-2 rounded">
            <span>{{ activeIndex + 1 }} / {{ props.product?.images?.length }}</span>
          </div>
        </div>
      </div>

      <div v-if="app.isLargeDesktopScreen" class="aside-carousel relative">
        <swiper-container
          ref="asideCarouselRef"
          class="!md:h-376px !lg:h-426px xl:!h-596px"
          v-bind="ASIDE_CAROUSEL_SWIPER_OPTIONS"
          @swiperafterinit="handleAsideCarouselSwiperInit"
          @swiperslidechange="handleAsideCarouselSwiperSlideChange"
        >
          <swiper-slide v-for="(img, idx) in props.product.images" :key="img" class="!w-92px !h-92px cursor-pointer">
            <nd-img :src="`fn:${img}`" class="aspect-1 object-cover bg-gray-30 rounded" @click="handleClickAsideCarouselImage(idx)" />
          </swiper-slide>
        </swiper-container>
        <button :id="prevBtnId" class="swiper-button-prev absolute top-0 left-0 w-92px h-22px flex justify-center z-1 transition-opacity">
          <svg-icon name="arrow-up" class="w-8 h-8 p-1.5 -mt-1.5" />
        </button>
        <button :id="nextBtnId" class="swiper-button-next absolute bottom-0 left-0 w-92px h-22px flex justify-center z-1 transition-opacity">
          <svg-icon name="arrow-down" class="w-8 h-8 p-1.5 -mt-1.5" />
        </button>
      </div>

      <template #fallback>
        <n-skeleton class="aspect-1 h-full sm:w-600px md:w-370px lg:w-426px xl:w-596px" />
      </template>
    </client-only>
  </section>
</template>

<script setup lang="ts">
import type { Swiper } from 'swiper'
import { NSkeleton } from 'naive-ui'

import { useAppStore } from '@/store/app'

import FavoriteButton from '@/components/common/favorite-button.vue'

import type { UiProductInfo } from '@/types/product'

const galleryID = 'product-gallery-main-image'
const prevBtnId = `prev-btn-${getRandomString()}`
const nextBtnId = `next-btn-${getRandomString()}`

const { lightboxInit } = useLightbox(galleryID)

const app = useAppStore()

const props = defineProps<{
  product: UiProductInfo
}>()

const activeIndex = ref(0)

const mainImageSwiper = ref<Swiper>()
const MAIN_IMAGE_SWIPER_OPTIONS = {
  slidesPerView: 1,
  spaceBetween: 16,
  pagination: false,
}
const handleMainImageSwiperInit = (e: CustomEvent) => {
  const swiperInstance = e.detail[0]
  mainImageSwiper.value = swiperInstance

  lightboxInit()
}
const handleMainImageSwiperSlideChange = () => {
  if (!mainImageSwiper.value) return

  syncIndex(mainImageSwiper.value.activeIndex)
}

const asideCarouselSwiper = ref<Swiper>()
const ASIDE_CAROUSEL_SWIPER_OPTIONS = {
  slidesPerView: 'auto',
  slidesPerGroup: 1,
  spaceBetween: 16,
  direction: 'vertical',
  navigation: {
    nextEl: `#${nextBtnId}`,
    prevEl: `#${prevBtnId}`,
    disabledClass: `opacity-0`,
  },
}
const handleAsideCarouselSwiperInit = (e: CustomEvent) => {
  const swiperInstance = e.detail[0]
  asideCarouselSwiper.value = swiperInstance
}
const handleAsideCarouselSwiperSlideChange = () => {
  if (!asideCarouselSwiper.value) return

  syncIndex(asideCarouselSwiper.value.activeIndex)
}
const handleClickAsideCarouselImage = (idx: number) => {
  if (!asideCarouselSwiper.value) return

  syncIndex(idx)
}

const syncIndex = (idx: number) => {
  activeIndex.value = idx

  if (!mainImageSwiper.value || !asideCarouselSwiper.value) return
  mainImageSwiper.value.slideTo(idx)
  asideCarouselSwiper.value.slideTo(idx)
}
</script>

<style lang="scss" scoped>
.gallery {
  .swiper-button-prev {
    background: linear-gradient(0, rgba(255, 255, 255, 0) 0%, #fff 100%);
  }
  .swiper-button-next {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
  }
}
</style>
